import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';

interface Answer {
  question_id: string;
  set_id: string;
  star_rating?: number;
  text_answer?: string;
  selected_option?: string;
  _id: string;
}

interface Response {
  customer_id: string;
  customer_name: string;
  answers: Answer[];
  remarks: string;
  conducted_date: string;
  conducted_by: string;
  conducted_by_employee_name?: string;
  _id: string;
}

interface Question {
  question_id: string;
  question_text: string;
  question_type: 'star_rating' | 'text' | 'multiple_choice';
  options: string[];
  is_required: boolean;
  _id: string;
}

interface QuestionnaireSet {
  set_id: string;
  set_name: string;
  questions: Question[];
  _id: string;
}

interface SurveyDetail {
  survey_id: string;
  survey_number: string;
  survey_title: string;
  survey_description: string;
  questionnaire_sets: QuestionnaireSet[];
  responses: Response[];
  status: string;
  created_date_time: string;
  total_assigned: number;
  total_completed: number;
  pending_count: number;
}

@Component({
  selector: 'app-feedback-detail',
  templateUrl: './feedback-detail.component.html',
  styleUrls: ['./feedback-detail.component.css']
})
export class FeedbackDetailComponent implements OnInit {
  surveyDetail: SurveyDetail | null = null;
  responses: Response[] = [];
  questionnaireMap: Map<string, Map<string, Question>> = new Map();
  loading = true;
  error = '';
  selectedResponse: Response | null = null;

  private baseUrl = 'https://test.shajipaints.com/dev/FactoryCustomerSurvey';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    const surveyId = this.route.snapshot.paramMap.get('survey_id');
    if (surveyId) {
      this.loadSurveyFeedback(surveyId);
    } else {
      this.error = 'Survey ID not provided';
      this.loading = false;
    }
  }

  loadSurveyFeedback(surveyId: string): void {
    this.loading = true;
    this.error = '';

    this.http.get<any>(`${this.baseUrl}/detail/${surveyId}`).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.surveyDetail = response.data;
          this.responses = response.data.responses || [];
          this.buildQuestionnaireMap();
          
          // Select first response by default if available
          if (this.responses.length > 0) {
            this.selectedResponse = this.responses[0];
          }
        } else {
          this.error = 'Failed to load survey feedback';
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading feedback:', err);
        this.error = 'Error loading survey feedback. Please try again.';
        this.loading = false;
      }
    });
  }

  buildQuestionnaireMap(): void {
    if (!this.surveyDetail) return;

    this.surveyDetail.questionnaire_sets.forEach(set => {
      const questionMap = new Map<string, Question>();
      set.questions.forEach(question => {
        questionMap.set(question.question_id, question);
      });
      this.questionnaireMap.set(set.set_id, questionMap);
    });
  }

  getQuestion(setId: string, questionId: string): Question | undefined {
    const setMap = this.questionnaireMap.get(setId);
    return setMap?.get(questionId);
  }

  selectResponse(response: Response): void {
    this.selectedResponse = response;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getStarArray(rating: number): number[] {
    return Array(5).fill(0).map((_, i) => i + 1);
  }

  getCompletionPercentage(): number {
    if (!this.surveyDetail || this.surveyDetail.total_assigned === 0) {
      return 0;
    }
    return Math.round((this.surveyDetail.total_completed / this.surveyDetail.total_assigned) * 100);
  }

  exportToExcel(): void {
    if (!this.surveyDetail) return;
    
    window.open(
      `${this.baseUrl}/export/${this.surveyDetail.survey_id}`,
      '_blank'
    );
  }

  goBack(): void {
    this.router.navigate(['admin/customer-survey-menu/feedback']);
  }
}